import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type DarkModeCtx = {
  isDarkMode: boolean;
  theme: "light" | "dark";
  setIsDarkMode: (v: boolean) => void;
  toggleDarkMode: () => void;
};

const DarkModeContext = createContext<DarkModeCtx | undefined>(undefined);

export const DarkModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("darkMode");
      if (stored != null) setIsDarkMode(stored === "true");
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("darkMode", String(isDarkMode));
    } catch {}
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  const value = useMemo<DarkModeCtx>(
    () => ({
      isDarkMode,
      theme: isDarkMode ? "dark" : "light",
      setIsDarkMode,
      toggleDarkMode: () => setIsDarkMode((p) => !p),
    }),
    [isDarkMode]
  );

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>;
};

export const useDarkMode = () => {
  const ctx = useContext(DarkModeContext);
  if (!ctx) throw new Error("useDarkMode must be used within DarkModeProvider");
  return ctx;
};
